import { iframeConstants as constants, QUERY_STRING_INSTALL_PAGE_URL } from "../../constants";
import { InstallPageEvent } from "../frames/frameMessages";
import { getCurrentUrl } from "../ServerApi/ApiUrls";
import { assertValidFunction } from "../types/assertions";
import { ViewModel } from "../types/types";
import { logger } from "../utils/logger/logger";
import { getVerificationUrl } from "./utils";

type IframeOptions = {
  className: string;
};

class Iframe {
  containerElement: HTMLElement;

  verificationUrl: URL;

  isMobileDevice: boolean;

  iframe: HTMLIFrameElement;

  verificationIframeUid: string;

  hasLoaded: boolean = false;

  onLoadEvents: Function[] = [];

  constructor(containerElement: HTMLElement, url: string) {
    if (Iframe.isValidHttpUrl(url)) {
      const options = {
        className: constants.CLASS_NAMES.INLINE_IFRAME_CONTENT,
      };
      this.containerElement = containerElement;
      this.verificationUrl = new URL(getVerificationUrl(url));
      this.verificationIframeUid = Iframe.createUniqueId();
      this.createIframe(options);
      this.addVerificationSizeUpdatesListener();
    } else {
      logger.error("Invalid URL. Provide a proper URL: https://example.com/", "iframe url");
    }
  }

  static createUniqueId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  static isValidHttpUrl(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  createIframe(options: IframeOptions): HTMLIFrameElement {
    this.iframe = document.createElement("iframe");
    this.iframe.classList.add(options.className);
    this.iframe.title = this.verificationUrl.href;
    let mark = "?";
    if (this.verificationUrl.search) {
      mark = "&";
    }
    this.iframe.src = `${this.verificationUrl.href}${mark}verificationIframeUid=${
      this.verificationIframeUid
    }&${QUERY_STRING_INSTALL_PAGE_URL}=${encodeURIComponent(getCurrentUrl())}`;
    this.iframe.onload = () => this.onLoad();
    return this.iframe;
  }

  // If iFrame hasn't loaded yet, queue up the callback
  // otherwise call it immediately
  addOnLoadEvent(callback: Function) {
    try {
      assertValidFunction(callback);
    } catch (e) {
      logger.error("Invalid callback provided to iFrame.onLoad()", e);
      return;
    }
    if (this.hasLoaded) {
      callback();
    } else {
      this.onLoadEvents.push(callback);
    }
  }

  onLoad() {
    this.hasLoaded = true;
    this.onLoadEvents.forEach((callback) => callback());
  }

  addVerificationSizeUpdatesListener() {
    window.addEventListener("message", (event) => {
      if (this.verificationUrl.origin !== event.origin) {
        return;
      }

      const message = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      if (message.verificationIframeUid !== this.verificationIframeUid) {
        return;
      }

      switch (message.action) {
        case "updateHeight":
          // UX-1129: If we are getting height updates, turn off scrolling for
          // the iframe. This prevents browsers (Safari in particular) from
          // re-wrapping because of different widths and constantly changing
          // it's own height. `scrolling = "no"` is deprecated, but supported in
          // all browsers and fixes the problem well.
          this.iframe.scrolling = "no";
          this.iframe.style.height = `${message.height}px`;
          break;
        default:
          logger.error("Unsupported message.action");
      }
    });
  }

  setViewModel(viewModel: Partial<ViewModel> | ViewModel) {
    const message: InstallPageEvent = {
      action: "setViewModel",
      viewModel,
    };

    this.addOnLoadEvent(() =>
      this.iframe.contentWindow.postMessage(message, this.verificationUrl.origin),
    );
  }

  init() {
    this.containerElement.appendChild(this.iframe);
  }
}

export default Iframe;
